/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import {
  CardLoader,
  ChangePassword,
  CustomerInformationCard,
  EditCustomerInformation,
} from '@/account/components';
import { useCustomerInformation } from '@/account/hooks/containers/useCustomerInformation';
import { useInLineAlert } from '@/account/hooks/useInLineAlert';
import { CustomerInformationProps } from '@/account/types';
import { classes, Container } from '@adobe-commerce/elsie/lib';
import { Header } from '@adobe-commerce/elsie/components';
import { useText } from '@adobe-commerce/elsie/i18n';

export const CustomerInformation: Container<CustomerInformationProps> = ({
  className,
  withHeader = true,
  slots,
}) => {
  const translations = useText({
    containerTitle: 'Account.minifiedView.CustomerInformation.containerTitle',
  });
  const { inLineAlertProps, handleSetInLineAlert } = useInLineAlert();
  const {
    createdAt,
    errorPasswordEmpty,
    passwordValue,
    showPasswordOnEmailChange,
    orderedCustomerData,
    loading,
    normalizeFieldsConfig,
    submitLoading,
    showEditForm,
    showChangePassword,
    handleShowChangePassword,
    handleHideChangePassword,
    handleShowEditForm,
    handleHideEditForm,
    handleUpdateCustomerInformation,
    handleInputChange,
    handleSetPassword,
    handleOnBlurPassword,
  } = useCustomerInformation({ handleSetInLineAlert });

  if (loading)
    return (
      <div data-testid="customerInformationLoader">
        <CardLoader withCard />
      </div>
    );

  return (
    <div className={classes(['account-customer-information', className])}>
      {withHeader ? (
        <Header
          title={translations.containerTitle}
          divider={false}
          className={'customer-information__title'}
        />
      ) : null}
      <CustomerInformationCard
        createdAt={createdAt}
        slots={slots}
        orderedCustomerData={orderedCustomerData}
        showEditForm={showEditForm}
        showChangePassword={showChangePassword}
        handleShowChangePassword={handleShowChangePassword}
        handleShowEditForm={handleShowEditForm}
      />
      {showChangePassword ? (
        <ChangePassword
          inLineAlertProps={inLineAlertProps}
          handleSetInLineAlert={handleSetInLineAlert}
          handleHideChangePassword={handleHideChangePassword}
        />
      ) : null}
      {showEditForm ? (
        <EditCustomerInformation
          inLineAlertProps={inLineAlertProps}
          submitLoading={submitLoading}
          formFieldsList={normalizeFieldsConfig}
          errorPasswordEmpty={errorPasswordEmpty}
          passwordValue={passwordValue}
          showPasswordOnEmailChange={showPasswordOnEmailChange}
          handleSetPassword={handleSetPassword}
          handleOnBlurPassword={handleOnBlurPassword}
          handleUpdateCustomerInformation={handleUpdateCustomerInformation}
          handleHideEditForm={handleHideEditForm}
          handleInputChange={handleInputChange}
        />
      ) : null}
    </div>
  );
};
